import {
  CurrencyDetails,
  PriceFormatSettingWithTemplate,
  ProviderSignupSource,
  ProviderLifecycleStatus,
  MenuEditorSettings,
  DineOutSettings,
  DeductionsReportSettings,
} from '../../api/openapi_gen/deliveryProviderPortal/deliveryProvider/apis';

import { LocaleType } from '../../utils/translations_types';

import {
  PromotionAccessType,
  ProviderEligibility,
} from '../../api/openapi_gen/deliveryProviderPortal/deliveryCampaignProvider/apis';
import { TargetingValues } from '../../api/openapi_gen/deliveryProviderPortal/deliveryProviderPortal/models';
import { isWithMenuEditorControlValue } from '../../experiments/providerOnboarding';

export { AccountData } from '../../api/openapi_gen/deliveryProviderPortal/deliveryProvider/apis';

export interface VendorAssignedProvider {
  vendor_id: number;
  vendor_name: string;
  providers: ProviderPortalAssignedProvider[];
}

export interface ProviderPortalAssignedProvider {
  provider_id: number;
  provider_name: string;

  vendor_id: number;
  vendor_name: string;

  city_id: number;
  country_code: string;
  country_support_email: string;
  currency: CurrencyDetails;
  price_format_setting: PriceFormatSettingWithTemplate | null;
  city_locales: LocaleType[];
  city_main_locale: LocaleType;
  campaigns_settings?: CampaignsSettings;
  signup_source: ProviderSignupSource;
  lifecycle_status: ProviderLifecycleStatus;
  timezone: string;

  menu_editor_settings?: MenuEditorSettings;
  has_city_subject_to_bulgaria_euro_change: boolean;
  bulgaria_currency_change_timestamp?: number;
  dine_out_settings: DineOutSettings;
  deductions_report_settings: DeductionsReportSettings;
}

export interface SelectedProvider {
  providerId: number;
  providerName: string;

  vendorId: number;
  vendorName: string;

  cityId: number;
  countryCode: string;
  countrySupportEmail: string;
  currency: CurrencyDetails;
  timezone: string;
  city_locales: LocaleType[];
  city_main_locale: LocaleType;
  signup_source: ProviderSignupSource;
  lifecycle_status: ProviderLifecycleStatus;

  menuEditorSettings?: MenuEditorSettings;
  campaigns_settings?: CampaignsSettings;

  permissions: AccountPermissions;

  isSubjectToBulgariaEuroChange?: boolean;
  bulgariaEuroChangeTimestamp?: number;
}

export interface SelectedVendorAndProvider {
  providerId: number;
  providerName: string;

  vendorId: number;
  vendorName: string;
}

export interface CampaignsSettings {
  can_self_assign_campaign: boolean;
  can_manage_promotions?: boolean;
  eligibility?: ProviderEligibility[];
  is_menu_inflation?: boolean;
  can_setup_bolt_plus_promotions?: boolean;
  can_manage_smart_promotions?: boolean;
  promotion_access_type?: PromotionAccessType;
}

export interface AccountPermissions {
  menuEditorAccess: boolean;
  selfAssignCampaigns: boolean;
  canSeePromotions: boolean;
  canSeeSmartPromotions: boolean;
  canCreateFoodPromotions: boolean;
  canCreatePromotions: boolean;
  canCreatePaidVisibility: boolean;
  canSeeSponsoredListings: boolean;
  canSetupBoltPlusPromotions: boolean;
  dineOutAccess: boolean;
  deductionsAccess: boolean;
  canCreateSmartPromotionOnly: boolean;
  canCreateItemsPromotionOnly: boolean;
  canCreateAllPromotion: boolean;
  topPerformerPage: boolean;
}

export enum AccountStage {
  ONBOARDING = 'onboarding',
  ACTIVATED = 'activated',
}

export function mapAssignedProviderToSelectedProvider(
  provider: ProviderPortalAssignedProvider,
): SelectedProvider {
  const canCreatePaidVisibility = Boolean(
    provider.campaigns_settings?.eligibility?.includes(ProviderEligibility.PaidVisibility),
  );

  return {
    providerId: provider.provider_id,
    providerName: provider.provider_name,

    vendorId: provider.vendor_id,
    vendorName: provider.vendor_name,

    cityId: provider.city_id,
    countryCode: provider.country_code,
    countrySupportEmail: provider.country_support_email,
    timezone: provider.timezone,
    currency: provider.currency,

    city_locales:
      provider.city_locales && provider.city_locales.length
        ? provider.city_locales
        : [LocaleType.English],
    city_main_locale: provider.city_main_locale ?? LocaleType.English,

    menuEditorSettings: provider.menu_editor_settings,

    isSubjectToBulgariaEuroChange: provider.has_city_subject_to_bulgaria_euro_change,
    bulgariaEuroChangeTimestamp: provider.bulgaria_currency_change_timestamp,

    campaigns_settings: provider.campaigns_settings,
    signup_source: provider.signup_source,
    lifecycle_status: provider.lifecycle_status,

    permissions: {
      menuEditorAccess: Boolean(provider.menu_editor_settings?.is_enabled),
      selfAssignCampaigns: Boolean(provider.campaigns_settings?.can_self_assign_campaign),
      canSeePromotions: Boolean(
        provider.campaigns_settings?.can_self_assign_campaign ||
          provider.campaigns_settings?.can_manage_promotions,
      ),
      canSeeSmartPromotions: Boolean(provider.campaigns_settings?.can_manage_smart_promotions),
      canCreateFoodPromotions: !provider.campaigns_settings?.is_menu_inflation,
      canCreatePromotions: Boolean(
        provider.campaigns_settings?.eligibility?.includes(ProviderEligibility.Promotion),
      ),
      canCreatePaidVisibility,
      canSeeSponsoredListings: canCreatePaidVisibility,
      canSetupBoltPlusPromotions: Boolean(
        provider.campaigns_settings?.can_setup_bolt_plus_promotions,
      ),
      dineOutAccess: Boolean(provider.dine_out_settings?.is_enabled),
      deductionsAccess: provider.deductions_report_settings.is_deductions_report_enabled,
      canCreateSmartPromotionOnly: Boolean(
        provider.campaigns_settings?.promotion_access_type === PromotionAccessType.SMART,
      ),
      canCreateItemsPromotionOnly: Boolean(
        provider.campaigns_settings?.promotion_access_type === PromotionAccessType.SELECTED_ITEMS,
      ),
      canCreateAllPromotion: Boolean(
        provider.campaigns_settings?.promotion_access_type === PromotionAccessType.ALL,
      ),
      topPerformerPage: false,
    },
  };
}

// FIXME: this still feels dirty. If permissions can depend on targeting, then we should
//  ideally not include permissions in `SelectedProvider` above and calculate that in a
//  separate store value after targeting params are loaded.
export const buildProviderPermissionsWithOverrides = (
  provider: SelectedProvider,
  targetingParameters: TargetingValues,
): AccountPermissions => {
  const isWithMenuEditor =
    targetingParameters.provider_onboarding.withMenuEditor ?? isWithMenuEditorControlValue;
  return {
    ...provider.permissions,
    menuEditorAccess: Boolean(
      provider.permissions.menuEditorAccess ||
        (isWithMenuEditor && provider.lifecycle_status === ProviderLifecycleStatus.ONBOARDING),
    ),
    topPerformerPage: Boolean(
      targetingParameters.delivery_provider_portal_flags.show_top_performer_content,
    ),
  };
};

export function mapSelectedProviderDataToSelectedVendorAndProvider(
  provider: SelectedProvider,
): SelectedVendorAndProvider {
  return {
    providerId: provider.providerId,
    providerName: provider.providerName,
    vendorId: provider.vendorId,
    vendorName: provider.vendorName,
  };
}

export function getSelectedProviderData(
  selectedProvider: number,
  assignedProviders: ProviderPortalAssignedProvider[],
): SelectedProvider | null {
  if (!assignedProviders?.length) {
    return null;
  }

  const firstProvider = assignedProviders[0];

  // if we do not have selected provider, we return first 1 as selected
  if (!selectedProvider) {
    return mapAssignedProviderToSelectedProvider(firstProvider);
  }

  // we search for selected provider
  const providerData = assignedProviders?.find((item) => item.provider_id === selectedProvider);

  // if we do not have selected provider, we return first 1 as selected
  return mapAssignedProviderToSelectedProvider(providerData || firstProvider);
}
